/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import { TextInputType, NumberValueType } from "../../../../data/formControlTypes";
import { PatternFormat } from 'react-number-format';
import "./Input.scss"
import { formatCurrency, formatPercentage, localStringToNumber } from "../../../../lib/InputMasking";

const excptIntSymbol = ["e", "E", "+", "-", "."];
const excptDecimalSymbol = ["e", "E", "+", "-"];

const Input = ({
  type = TextInputType.TEXT,
  name = "",
  placeholder = `Please Enter ${name}`,
  value,
  min,
  max,
  onChange,
  onKeyup,
  onBlur,
  minLength,
  maxLength,
  cssClass = "input-field",
  allowSpace = true,
  valueType,
  isDisable,
  isReadOnly,
  pattern,
  maskFormat,
  extendedFormat,
  minValueLength,
  masking,
}) => {

  const [format, setFormat] = useState(maskFormat);
  const [inputAttributes, setInputAttributes] = useState({});
  const [displayValue, setDisplayValue] = useState(value);
  const [inputValue, setInputValue] = useState(localStringToNumber(value));

  const initialLoadRef = useRef(true);

  const updateAttributes = () => {
    const newAttribute = { ...inputAttributes };

    if (minLength) {
      newAttribute.minLength = minLength;
    }

    if (maxLength) {
      newAttribute.maxLength = maxLength;
    }

    if (isReadOnly) {
      newAttribute.readOnly = isReadOnly;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.INT) {
      newAttribute.step = 1;
    }

    setInputAttributes(newAttribute);
  };

  useEffect(() => {
    updateAttributes();
  }, [minLength, maxLength, type, valueType]);

  useEffect(() => {
    if (masking?.isMasking && value) {
      initialLoadRef.current = false;
      onHandleMasking(value);
    }
  }, [masking, value]);

  useEffect(() => {
    if (maskFormat) {
      const unMaskedValue = value?.replace(/\D/g, '');
      setFormat(unMaskedValue.length >= minValueLength ? extendedFormat : maskFormat);
    }
  }, [value]);

  const handleInputChange = (e) => {
    let inputValue = e.target.value;

    if (masking?.isMasking) {
      const newValue = e.target?.value?.replace(/[^0-9.]/g, '');
      setInputValue(newValue ? localStringToNumber(newValue) : newValue);
      setDisplayValue(newValue);
    }

    if (type === TextInputType.NUMBER && maxLength) {
      inputValue = inputValue.slice(0, maxLength);
      e.target.value = inputValue;
    }

    onChange(e);

    const unMaskedValue = inputValue?.replace(/\D/g, '');
    setFormat(unMaskedValue.length >= minValueLength ? extendedFormat : maskFormat);
  };

  const handleBlur = () => {
    onHandleMasking(inputValue);
    if (onBlur) onBlur();
  };

  const handleFocus = () => {
    setDisplayValue(value);
  };

  const onHandleMasking = (value) => {
    if (masking?.isMasking && value) {
      let formattedValue = value;
      if (masking.maskingType === "currency") {
        formattedValue = formatCurrency(value, masking.countryCode);
      } else if (masking.maskingType === "percent") {
        formattedValue = formatPercentage(value);
      }
      setInputValue(value);
      setDisplayValue(formattedValue);
    }
  }

  const handleKeyDown = (e) => {
    if (!allowSpace && e.keyCode === 32) {
      e.preventDefault();
      return;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.INT && excptIntSymbol.includes(e.key)) {
      e.preventDefault();
      return;
    }

    if (type === TextInputType.NUMBER && valueType === NumberValueType.DECIMAL && excptDecimalSymbol.includes(e.key)) {
      e.preventDefault();
    }
  };
  return (
    <>
      {maskFormat
        ?
        <PatternFormat
          mask="_"
          id={name}
          value={value}
          name={name}
          type={type}
          className={cssClass}
          placeholder={placeholder}
          onChange={handleInputChange}
          onKeyUp={onKeyup}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isDisable}
          format={format}
          pattern={pattern}
          {...inputAttributes}
        />
        :
        <input
          id={name}
          // value={value}
          value={masking?.isMasking ? displayValue : value}
          name={name}
          type={type}
          className={cssClass}
          placeholder={placeholder}
          onChange={handleInputChange}
          onKeyUp={onKeyup}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={isDisable}
          min={min}
          max={max}
          {...inputAttributes}
        />
      }

    </>
  );
};

Input.propTypes = {
  type: PropTypes.oneOf([
    TextInputType.TEXT,
    TextInputType.EMAIL,
    TextInputType.PASSWORD,
    TextInputType.NUMBER,
  ]).isRequired,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onKeyup: PropTypes.func,
  onBlur: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  cssClass: PropTypes.string,
  allowSpace: PropTypes.bool,
  valueType: PropTypes.oneOf([
    NumberValueType.INT,
    NumberValueType.DECIMAL,
  ]),
  isDisable: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  pattern: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(RegExp),
  ]),
  maskFormat: PropTypes.string,
  extendedFormat: PropTypes.string,
  minValueLength: PropTypes.number,
  masking: PropTypes.shape({
    isMasking: PropTypes.bool.isRequired,
    maskingType: PropTypes.array.isRequired,
    countryCode: PropTypes.string,
  }),
};

export default Input;